import type { DomainEvent } from '@/core/events/types';
import type { GameSave } from '@/core/persistence/schema/v1';
import type { HandlerEnv } from './placement/handlers';

const inc = (draft: GameSave, key: string, by = 1) => {
  draft.stats.counters[key] = (draft.stats.counters[key] ?? 0) + by;
};

const addDistinct = (draft: GameSave, key: string, value: string) => {
  const list = draft.stats.distinct[key] ?? [];
  if (!list.includes(value)) draft.stats.distinct[key] = [...list, value];
};

/**
 * Durable counters behind achievements and suggestions (content-schemas §12.3).
 * Undo/redo never count, so nothing can be farmed by toggling.
 */
export function statsSystem(draft: GameSave, events: readonly DomainEvent[], env: HandlerEnv): DomainEvent[] {
  for (const e of events) {
    switch (e.type) {
      case 'ITEM_PLACED':
        if (e.origin !== 'player' || e.kind === 'product') break;
        inc(draft, 'itemsPlaced');
        if (e.rightSpot) {
          inc(draft, 'rightSpotPlacements');
          addDistinct(draft, 'rightSpotItemIds', e.itemId);
        }
        if (e.rotation !== 0) inc(draft, 'itemsRotated');
        break;
      case 'ITEM_ROTATED':
        if (e.origin !== 'player') break;
        inc(draft, 'itemsRotated');
        if (e.rightSpot) {
          inc(draft, 'rightSpotPlacements');
          addDistinct(draft, 'rightSpotItemIds', e.itemId);
        }
        break;
      case 'ITEM_ACQUIRED': {
        addDistinct(draft, 'itemsEverOwned', e.itemId);
        const kind = env.content.items.get(e.itemId)?.kind;
        if (kind === 'decor' || kind === 'furniture') addDistinct(draft, 'ownedDecorFurnitureIds', e.itemId);
        break;
      }
      case 'PRODUCT_DISPLAYED':
        if (e.origin !== 'player') break;
        inc(draft, 'productsDisplayed');
        if (e.rightSpot) {
          inc(draft, 'rightSpotPlacements');
          addDistinct(draft, 'rightSpotItemIds', e.itemId);
        }
        break;
      case 'SALE_COMPLETED':
        inc(draft, 'salesCount');
        inc(
          draft,
          'unitsSold',
          e.lines.reduce((sum, l) => sum + l.qty, 0),
        );
        break;
      case 'BOX_OPENED':
        inc(draft, 'boxesOpened');
        break;
      case 'MINIGAME_COMPLETED':
        if (e.completed) inc(draft, 'miniGamesCompleted');
        break;
      case 'STOCK_PURCHASED':
        inc(draft, 'stockBoxesBought', e.boxes);
        break;
      case 'ITEM_PURCHASED':
        inc(draft, 'itemsPurchased', e.qty);
        break;
      case 'CHAPTER_SEEN':
        if (e.firstTime) inc(draft, 'chaptersSeen');
        break;
      case 'SET_COMPLETED':
        addDistinct(draft, 'setsCompleted', e.setId);
        break;
      case 'CAT_PETTED':
        inc(draft, 'catPets');
        break;
      case 'SHOOT_COMPLETED':
        inc(draft, 'shootsCompleted');
        if (e.grade === 'excellent') inc(draft, 'shootsExcellent');
        break;
      case 'PHOTO_TAKEN':
        inc(draft, 'photosTaken');
        break;
      case 'MAIL_CLAIMED':
        inc(draft, 'mailClaimed');
        break;
      case 'ORDER_FULFILLED':
        inc(draft, 'ordersFulfilled');
        break;
      case 'ORDER_SKIPPED':
        inc(draft, 'ordersSkipped');
        break;
      case 'NPC_TALKED':
        if (e.initiatedBy !== 'player') break;
        inc(draft, 'npcTalks');
        addDistinct(draft, 'npcsTalkedTo', e.npcId);
        break;
      case 'FRIENDSHIP_LEVEL_REACHED':
        inc(draft, 'friendshipLevels');
        break;
      default:
        break;
    }
  }
  return [];
}
